/**
 * RBAC Redux Slice
 *
 * Stores the current user's permission config and identity context.
 * Syncs automatically when authSlice dispatches setCurrentUser / setTokenAndUser / logout.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/stores/store";
import {
  logout,
  setCurrentUser,
  setTokenAndUser,
} from "@/stores/slices/authSlice";
import {
  deriveUserContext,
  hydrateRbacFromUser,
  unwrapUser,
  type AccessConfig,
  type RbacUserContext,
} from "@/rbac/utils";
import { isAdminPresetName } from "@/features/users/hooks/computeRoleCounts";

/** Admins / top role / admin preset → skip fine-grained `access_config` + route-key UI gates. */
function userSkipsFineGrainedRbacUi(
  ctx: RbacUserContext | null | undefined
): boolean {
  if (!ctx) return false;
  if (ctx.isSystemAdmin || ctx.isTenantAdmin) return true;
  const sr = ctx.systemRole;
  if (sr === "SYSTEM_ADMIN" || sr === "PLATFORM_STAFF" || sr === "TENANT_ADMIN")
    return true;
  if (ctx.roleHierarchy === 1) return true;
  if (ctx.presetRole === "admin") return true;
  return false;
}

// ─── State Shape ─────────────────────────────────────────────────

export interface RbacState {
  currentUser: any;
  accessConfig: AccessConfig | null;
  context: RbacUserContext | null;
}

// ─── Initial State (hydrate from localStorage) ──────────────────

const EMPTY: RbacState = {
  currentUser: null,
  accessConfig: null,
  context: null,
};

function getInitialState(): RbacState {
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("xale_current_user")
        : null;
    if (!raw) return EMPTY;
    const hydrated = hydrateRbacFromUser(unwrapUser(JSON.parse(raw)));
    return { ...EMPTY, ...hydrated };
  } catch {
    return EMPTY;
  }
}

// ─── Slice ───────────────────────────────────────────────────────

const rbacSlice = createSlice({
  name: "rbac",
  initialState: getInitialState(),
  reducers: {
    setAccessConfig(state, action: PayloadAction<AccessConfig | null>) {
      state.accessConfig = action.payload;
      if (state.context) {
        state.context = {
          ...state.context,
          presetRole: action.payload?.presetRole ?? null,
        };
      }
    },
    clearAccessConfig(state) {
      state.accessConfig = null;
      if (state.context) {
        state.context = { ...state.context, presetRole: null };
      }
    },
    resetRbacState(state) {
      state.currentUser = null;
      state.accessConfig = null;
      state.context = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCurrentUser, (state, action) => {
        Object.assign(state, hydrateRbacFromUser(unwrapUser(action.payload)));
      })
      .addCase(setTokenAndUser, (state, action) => {
        Object.assign(
          state,
          hydrateRbacFromUser((action.payload as any)?.user ?? null)
        );
      })
      .addCase(logout, (state) => {
        state.currentUser = null;
        state.accessConfig = null;
        state.context = null;
      });
  },
});

// ─── Actions ─────────────────────────────────────────────────────

export const { setAccessConfig, clearAccessConfig, resetRbacState } =
  rbacSlice.actions;
export default rbacSlice.reducer;

// ─── Selectors ───────────────────────────────────────────────────

export const selectRbacState = (state: RootState) => state.rbac;
export const selectAccessConfig = (state: RootState) => state.rbac.accessConfig;
export const selectRbacCurrentUser = (state: RootState) =>
  state.rbac.currentUser;
export const selectRbacContext = (state: RootState) => state.rbac.context;
export const selectRouteKeys = (state: RootState): string[] =>
  state.rbac.accessConfig?.routeKeys ?? [];
export const selectPresetRole = (state: RootState): string | null =>
  state.rbac.accessConfig?.presetRole ?? state.rbac.context?.presetRole ?? null;
export const selectIsTenantAdmin = (state: RootState): boolean =>
  Boolean(state.rbac.context?.isTenantAdmin);
export const selectIsSystemAdmin = (state: RootState): boolean =>
  Boolean(state.rbac.context?.isSystemAdmin);

/**
 * ARCHIVING leads is an admin-scoped action — same gate as bulk Trash:
 * tenant admin OR platform/system admin. The server enforces the same rule
 * (`leadService.bulkArchive` → `PlatformPolicyBase.ensureAtLeastTenantAdmin()`
 * on the `archive: true` arm), so an archive control shown to a regular user
 * would 403. Gate EVERY archive surface on this — the bulk toolbars on
 * Leads / stage routes / favourite views, and the lead-drawer menu item.
 *
 * RESTORE (`archive: false`) is deliberately NOT gated: the Archived leads
 * panel stays usable by any role that can reach it, so don't reuse this
 * selector to hide restore controls.
 */
export const selectCanArchiveLeads = (state: RootState): boolean =>
  Boolean(state.rbac.context?.isTenantAdmin) ||
  Boolean(state.rbac.context?.isSystemAdmin);

/**
 * FORCE a bulk stage update (skip the target stage's requirement checklist;
 * the multiple-deals check still applies) — admin-scoped, same audience as
 * archive: tenant admin
 * OR platform/system admin. The server enforces the identical rule
 * (`leadController.bulkStageUpdate` → `PlatformPolicyBase.isAdminScopedUser`,
 * re-checked in the bulk scenario), so never offer the toggle to anyone this
 * returns false for.
 */
export const selectCanForceStageUpdate = (state: RootState): boolean =>
  Boolean(state.rbac.context?.isTenantAdmin) ||
  Boolean(state.rbac.context?.isSystemAdmin);

/**
 * SHARE a list view (the "Share" action beside Export — a link to this exact
 * screen, plus handing it to people in the workspace by email). Admin-scoped:
 * tenant admin OR platform/system admin, mirroring the server's
 * `ensureAtLeastTenantAdmin()` on POST /core/shared-views and
 * /core/shared-views/:token/share. OPENING a shared link is NOT gated on
 * this — recipients are ordinary users, and what they see still flows through
 * their own LeadScope / RBAC.
 */
export const selectCanShareListViews = (state: RootState): boolean =>
  Boolean(state.rbac.context?.isTenantAdmin) ||
  Boolean(state.rbac.context?.isSystemAdmin);

/**
 * Mirrors the server's `assertSuperAdmin` gate for tenant lead-config writes
 * (LeadFormConfig): tenant admin OR a genuine SYSTEM_ADMIN. Deliberately
 * EXCLUDES PLATFORM_STAFF — `isSystemAdmin` is true for platform staff, but the
 * server rejects their writes, so gating edit controls on `isSystemAdmin` would
 * show an enabled toggle that 403s. Use this for "can edit tenant lead settings".
 */
export const selectCanEditTenantLeadConfig = (state: RootState): boolean => {
  const ctx = state.rbac.context;
  return Boolean(ctx?.isTenantAdmin) || ctx?.systemRole === "SYSTEM_ADMIN";
};
export const selectRbacTenantId = (state: RootState): number | null =>
  state.rbac.context?.tenantId ?? null;
export const selectRbacUserId = (state: RootState): number | null =>
  state.rbac.context?.userId ?? null;
export const selectRbacRoleId = (state: RootState): number | null =>
  state.rbac.context?.roleId ?? null;

/**
 * True → `usePermissions` / `<Can>` treat the user as allowed without reading `access_config`.
 * Uses `context`, then re-derives from `currentUser`, then `accessConfig.presetRole` edge case.
 */
export const selectCanBypassRbacChecks = (state: RootState): boolean => {
  const { context, currentUser, accessConfig } = state.rbac;
  if (userSkipsFineGrainedRbacUi(context)) return true;
  if (userSkipsFineGrainedRbacUi(deriveUserContext(currentUser))) return true;
  if (accessConfig?.presetRole === "admin") return true;
  return false;
};

/**
 * Who is EXEMPT from the org-wide lead-phone mask (Settings → Manage → Phone
 * Number Privacy). Deliberately NARROWER than `selectCanBypassRbacChecks`:
 *
 *   exempt = tenant admin (super admin) | genuine SYSTEM_ADMIN | ADMIN-preset role
 *
 * `selectCanBypassRbacChecks` — which this used to reuse — also exempts anyone
 * whose role sits at `roleHierarchy === 1` and anyone with systemRole
 * PLATFORM_STAFF. Hierarchy 1 is just the TOP role in the tenant's ladder; on
 * most tenants that is a Manager or a Counsellor Head, NOT an admin. Those
 * users kept seeing full numbers with the setting on, which is exactly the
 * "it isn't working" report — the top of the sales ladder, i.e. the people the
 * tenant most wanted covered, were silently exempt.
 *
 * Keep this separate from `selectCanBypassRbacChecks`: that selector drives
 * `usePermissions` / `<Can>` across the whole app, and narrowing it would
 * revoke UI from users who legitimately bypass fine-grained gates.
 */
export const selectIsLeadPhoneMaskExempt = (state: RootState): boolean => {
  const ctx = state.rbac.context;
  if (ctx?.isTenantAdmin) return true;
  if (ctx?.systemRole === "SYSTEM_ADMIN") return true;
  return isAdminPresetName(
    state.rbac.accessConfig?.presetRole ?? ctx?.presetRole ?? null,
  );
};

/**
 * Who KEEPS the Board tab when the org turns on Settings → Manage → "Disable
 * board view":
 *
 *   exempt = super admin (tenant admin) | platform/system admin | ADMIN-preset role
 *
 * The provision is about which views a TEAM works from, not about who may see
 * the leads (the board endpoint is open and serves exactly what the list
 * serves), so admins — the people who set it up and who have to answer for the
 * pipeline — never lose the board. Wider than
 * `selectIsLeadPhoneMaskExempt` on purpose: that one excludes PLATFORM_STAFF
 * because the server 403s their writes, but nothing is written or revealed
 * here, so support staff keep the board too.
 *
 * Consume it through `useBoardViewDisabled()`, never on its own — the tab
 * list, the saved-pick bounce and the kanban render all read that one hook, so
 * they can never disagree about who sees a board.
 */
export const selectIsBoardViewHideExempt = (state: RootState): boolean => {
  const ctx = state.rbac.context;
  if (ctx?.isTenantAdmin || ctx?.isSystemAdmin) return true;
  return isAdminPresetName(
    state.rbac.accessConfig?.presetRole ?? ctx?.presetRole ?? null,
  );
};

/**
 * True when the user's ROLE opted into receiving branchless leads — the role
 * wizard's "Inbound — receive branchless leads" toggle
 * (`assignation_management.inbound_branchless = "allow"`). Such users hold
 * no-branch leads, so the leads page surfaces the "No branch" filter for them
 * too (the server's `canUseNoBranchFilter` applies the same rule).
 */
export const selectCanReceiveBranchlessLeads = (state: RootState): boolean => {
  const am = state.rbac.accessConfig?.global?.assignation_management;
  return am?.inbound_branchless === "allow";
};
